import { useState, useEffect } from 'react';
import svgPaths from '../../imports/svg-l7usstgz1l';

function Clouds() {
  return (
    <div className="relative shrink-0 w-full">
      <div className="content-stretch flex flex-col items-start relative">
        <div className="relative shrink-0 h-[8.054px] w-[19.2px]">
          <svg
            className="absolute block size-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 19.2001 8.05365"
          >
            <g>
              <path d={svgPaths.p32a55d00} fill="#F2F7F8" />
              <path d={svgPaths.p8682580} fill="#F2F7F8" />
              <path d={svgPaths.pbfddf00} fill="white" />
              <path d={svgPaths.p30ce8300} fill="#CEEAF0" />
              <path d={svgPaths.p2e338980} fill="white" />
              <path d={svgPaths.p6d59280} fill="#CEEAF0" />
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
}

function CloudsBig() {
  return (
    <div className="relative shrink-0 w-[20.16px] h-[9.644px]">
      <svg
        className="absolute block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 20.16 9.64424"
      >
        <g>
          <path d={svgPaths.p3205f700} fill="#E9F4F6" />
          <path d={svgPaths.p37157800} fill="#CEEAF0" />
          <path d={svgPaths.p1f67ed00} fill="white" />
          <path d={svgPaths.p4a62e00} fill="#E9F4F6" />
          <path d={svgPaths.p1fd80400} fill="white" />
          <path d={svgPaths.p6485b00} fill="white" />
          <path d={svgPaths.p2de7d380} fill="#E9F4F6" />
        </g>
      </svg>
    </div>
  );
}

function TotemIcon({ color }: { color: string }) {
  return (
    <div className="block overflow-clip relative shrink-0 size-[16px]">
      <div className="absolute inset-[8.33%_4.17%]">
        <svg
          className="absolute block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 14.6667 13.3333"
        >
          <path d={svgPaths.p1e53dd80} fill={color} />
        </svg>
      </div>
      <div className="absolute inset-[47.92%_0_8.33%_62.5%]">
        <svg
          className="absolute block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 6 6.99989"
        >
          <path d={svgPaths.p3bce680} fill={color} />
        </svg>
      </div>
    </div>
  );
}

export function ModeSwitch({
  className,
  onChange,
}: {
  className?: string;
  onChange?: (mode: 'lucent' | 'indigo') => void;
}) {
  const [manualMode, setManualMode] = useState<'lucent' | 'indigo' | null>(null);
  const [systemPreference, setSystemPreference] = useState<'lucent' | 'indigo'>('lucent');

  // Detect system color scheme
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setSystemPreference(e.matches ? 'indigo' : 'lucent');
    };
    handler(mq);
    mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    return () => mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
  }, []);

  const isSystemMode = manualMode === null;
  const resolvedMode = isSystemMode ? systemPreference : manualMode;
  const isIndigo = resolvedMode === 'indigo';
  const isLucent = resolvedMode === 'lucent';

  // Notify parent of resolved mode changes
  useEffect(() => {
    onChange?.(resolvedMode);
  }, [resolvedMode, onChange]);

  const toggle = () => {
    // Manual toggle disables system mode
    const next = isLucent ? 'indigo' : 'lucent';
    setManualMode(next);
  };

  const toggleSystemLock = () => {
    if (isSystemMode) {
      // Already in system mode, switch to manual with current resolved value
      setManualMode(resolvedMode);
    } else {
      // Enable system mode
      setManualMode(null);
    }
  };

  return (
    <div className={className || 'relative size-[64px]'}>
      <div className="flex flex-col items-center justify-center size-full">
        <div
          className={`content-stretch flex flex-col items-center justify-center p-[2px] relative size-full ${isIndigo ? 'gap-[8px]' : 'gap-[4px]'}`}
        >
          {/* Track */}
          <div
            className="h-[24px] relative rounded-[100000000px] shrink-0 w-full cursor-pointer"
            onClick={toggle}
            style={
              isIndigo
                ? {
                    backgroundImage:
                      'linear-gradient(122.75deg, rgb(39, 33, 69) 23.584%, rgb(23, 20, 41) 93.509%)',
                  }
                : {
                    backgroundImage:
                      'linear-gradient(122.75deg, rgb(254, 254, 255) 23.584%, rgb(219, 219, 220) 93.509%)',
                  }
            }
          >
            {/* Lucent state */}
            {isLucent && (
              <div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full">
                <div className="content-stretch flex items-center p-[2px] relative size-full">
                  {/* Knob */}
                  <div
                    className="content-stretch flex items-center justify-center relative rounded-[100000000px] shadow-[0px_3.2px_6.4px_0px_rgba(0,0,0,0.2),0px_1.6px_3.2px_0px_rgba(0,0,0,0.15),0px_0.8px_1.6px_0px_rgba(0,0,0,0.1)] shrink-0 size-[19.2px]"
                    style={{
                      backgroundImage:
                        'linear-gradient(145deg, rgb(254, 254, 255) 6.1733%, rgb(246, 250, 254) 93.827%)',
                    }}
                  >
                    <div className="relative shrink-0 size-[12px]">
                      <div className="bg-clip-padding border-0 border-[transparent] border-solid overflow-clip relative rounded-[inherit] size-full">
                        <div className="absolute inset-[8.33%]">
                          <div className="absolute inset-[-5%]">
                            <svg
                              className="block size-full"
                              fill="none"
                              preserveAspectRatio="none"
                              viewBox="0 0 11 11"
                            >
                              <path d={svgPaths.p26721280} stroke="#B6BECE" strokeLinecap="round" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_-1.6px_3.2px_0px_rgba(0,0,0,0.1),inset_0px_-0.1px_3.2px_0px_#c0b6ed]" />
                  </div>
                  {/* Scene - clouds */}
                  <div className="flex-[1_0_0] min-h-px min-w-px opacity-50 relative">
                    <div className="overflow-clip rounded-[inherit] size-full">
                      <div className="content-stretch flex flex-col items-start px-px relative w-full">
                        <Clouds />
                        <div className="relative shrink-0 w-full">
                          <div className="flex flex-col items-end size-full">
                            <div className="content-stretch flex flex-col items-end relative w-full">
                              <CloudsBig />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Indigo border */}
            {isIndigo && (
              <div
                aria-hidden="true"
                className="absolute border-[#171429] border-[0.4px] border-solid inset-[-0.4px] pointer-events-none rounded-[100000000.4px]"
              />
            )}

            {/* Indigo state */}
            {isIndigo && (
              <div className="flex flex-row justify-end size-full">
                <div className="content-stretch flex items-start justify-end p-[2px] relative size-full">
                  {/* Stars scene */}
                  <div className="content-stretch flex flex-[1_0_0] flex-col h-full items-start min-h-px min-w-px overflow-clip relative">
                    <div className="h-[17.665px] relative shrink-0 w-[39.746px]">
                      <svg
                        className="absolute block size-full"
                        fill="none"
                        preserveAspectRatio="none"
                        viewBox="0 0 39.7458 17.6648"
                      >
                        <g>
                          <path d={svgPaths.p1a8d3800} fill="#FEF8DC" />
                          <path d={svgPaths.p3293380} fill="#FEF8DC" />
                          <path d={svgPaths.p20b9980} fill="#FEF8DC" />
                          <path d={svgPaths.pc9b4c80} fill="#FEF8DC" />
                          <path d={svgPaths.p1c6bf680} fill="#FEF8DC" />
                          <path d={svgPaths.p3b1ead00} fill="#FEF8DC" />
                          <path d={svgPaths.p1ee9f670} fill="#FEF8DC" />
                          <path d={svgPaths.p1cbbe80} fill="#FEF8DC" />
                          <path d={svgPaths.p81c8600} fill="#FEF8DC" />
                          <path d={svgPaths.pbfeb200} fill="#FEF8DC" />
                          <path d={svgPaths.p1e000ac0} fill="#FEF8DC" />
                        </g>
                      </svg>
                    </div>
                  </div>
                  {/* Moon knob */}
                  <div
                    className="content-stretch flex items-center justify-center p-[2px] relative rounded-[100000000px] shadow-[0px_4.8px_9.6px_0px_rgba(0,0,0,0.25),0px_3.2px_6.4px_0px_rgba(0,0,0,0.2),0px_1.6px_3.2px_0px_rgba(0,0,0,0.15),0px_0.8px_1.6px_0px_rgba(0,0,0,0.1)] shrink-0 size-[19.2px]"
                    style={{
                      backgroundImage:
                        'linear-gradient(145deg, rgb(39, 33, 69) 6.1733%, rgb(46, 52, 64) 93.827%)',
                    }}
                  >
                    <div className="relative shrink-0 size-[15.2px]">
                      <div className="bg-clip-padding border-0 border-[transparent] border-solid relative size-full">
                        <svg
                          className="absolute block size-full"
                          fill="none"
                          preserveAspectRatio="none"
                          viewBox="0 0 15.2 15.2"
                        >
                          <g opacity="0.7">
                            <path d={svgPaths.p1caab000} fill="#D9D3F6" />
                            <path d={svgPaths.p3fd7f80} fill="#505053" />
                            <path d={svgPaths.p32446240} fill="#D9D3F6" />
                            <path d={svgPaths.p20125200} fill="#505053" />
                            <path d={svgPaths.p7a18b00} fill="#D9D3F6" />
                            <path d={svgPaths.p1a6d6300} fill="#505053" />
                            <path d={svgPaths.p21a3d300} fill="#D9D3F6" />
                            <path d={svgPaths.p25bf3800} fill="#505053" />
                            <path d={svgPaths.p4a9c980} fill="#D9D3F6" />
                            <path d={svgPaths.p38a5d300} fill="#505053" />
                            <path d={svgPaths.p3297e600} fill="#D9D3F6" />
                            <path d={svgPaths.pa117680} fill="#505053" />
                            <path d={svgPaths.p2bcf51b0} fill="#D9D3F6" />
                            <path d={svgPaths.pc9f8e80} fill="#505053" />
                            <path d={svgPaths.p124a9880} fill="#D9D3F6" />
                            <path d={svgPaths.p26438280} fill="#505053" />
                            <path d={svgPaths.p13afee00} fill="#D9D3F6" />
                            <path d={svgPaths.p7c9d400} fill="#505053" />
                          </g>
                        </svg>
                      </div>
                    </div>
                    <div className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_-1.6px_3.2px_0px_rgba(0,0,0,0.1),inset_0px_1.2px_3.2px_0px_#c0b6ed]" />
                  </div>
                </div>
              </div>
            )}

            {/* Inner shadow overlay */}
            <div
              className={`absolute pointer-events-none ${
                isIndigo
                  ? 'inset-[-0.4px] rounded-[inherit] shadow-[inset_0px_-0.8px_0.8px_0px_rgba(254,254,255,0.2),inset_0px_-0.8px_0.8px_0px_rgba(255,255,255,0.25),inset_-0.8px_1.6px_1.6px_0px_rgba(0,0,0,0.25),inset_0px_1.6px_1.6px_0px_rgba(0,0,0,0.25),inset_0.8px_0.8px_1.6px_0px_rgba(0,0,0,0.25),inset_0px_1.6px_1.6px_0px_rgba(0,0,0,0.25),inset_-0.4px_1.6px_0.4px_0px_rgba(0,0,0,0.3)]'
                  : 'border-[#dbdbdc] border-[0.5px] border-solid inset-[-0.5px] rounded-[100000000.5px]'
              }`}
            />

            {/* Lucent outer shadow */}
            {isLucent && (
              <div
                aria-hidden="true"
                className="absolute inset-[-0.5px] pointer-events-none rounded-[inherit] shadow-[inset_0px_-0.8px_0.8px_0px_rgba(254,254,255,0.2),inset_0px_-0.8px_0.8px_0px_rgba(255,255,255,0.25),inset_-0.8px_1.6px_1.6px_0px_rgba(0,0,0,0.25),inset_0px_1.6px_1.6px_0px_rgba(0,0,0,0.25),inset_0.8px_0.8px_1.6px_0px_rgba(0,0,0,0.25),inset_0px_1.6px_1.6px_0px_rgba(0,0,0,0.25),inset_-0.4px_1.6px_0.4px_0px_rgba(0,0,0,0.3)]"
              />
            )}
          </div>

          {/* Label */}
          <div
            className="relative shrink-0 cursor-pointer"
            onClick={toggleSystemLock}
            title={
              isSystemMode
                ? 'Using system preference — click to switch manually'
                : 'Click to use system preference'
            }
          >
            <div className="flex flex-row items-center size-full">
              <div className="content-stretch flex gap-[2px] items-center relative">
                <div className="content-stretch flex items-center justify-center relative shrink-0">
                  <p
                    className="font-['Urbanist',sans-serif] leading-[normal] relative shrink-0 text-[11px] whitespace-nowrap"
                    style={{ fontWeight: 300, color: isIndigo ? '#fefeff' : '#b6bece' }}
                  >
                    {isSystemMode ? 'system' : isIndigo ? 'indigo' : 'lucent'}
                  </p>
                </div>
                <TotemIcon
                  color={
                    isSystemMode
                      ? isIndigo
                        ? '#c0b6ed'
                        : '#2d178b'
                      : isIndigo
                        ? '#0E0D46'
                        : '#B6BECE'
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
